/**
 * The splat shader effect behind Regions (regions.ts): one effect (key `regions`) that replaces
 * the separate Focus & Highlight (`focus`) and Annotation Layers (`annotations`) effects.
 *
 * Shader cost model (the annotation layers model, now for every region):
 * - Every drawable region is compiled in once. Outlines are baked into the shader source as
 *   straight-line code (outline-shape.ts); sphere / box areas read their transform from a uniform
 *   slot (`uRegionArea<k>`), so moving or animating them never recompiles; splat object areas read
 *   the splat's object flags (splat-objects.ts). Editing an area's shape recompiles once.
 * - Each region's colour rides in one vec4 (`uRegionColor<i>`: rgb + colourStrength × fade), its
 *   dim activation in a packed vec4 (`uRegionDim<k>`, four regions per vector), and the scene-wide
 *   surround look in `uRegionSurround` (x = brightness, y = saturation, z > 0 while any region
 *   dims). Showing, hiding, fading and recolouring a region are uniform writes only.
 * - A region whose colour and dim activation are both 0 skips its areas, so hidden regions cost
 *   nothing per splat.
 *
 * Per splat: colour each region in order, `mix(color, regionRgb, inside × strength × fade)`; then,
 * while any dimming region is on, `muted = mix(luma, color, S) × B` and
 * `color = mix(muted, color, insideAnyDimmingRegion)`. The CPU works out B and S each frame
 * (regionSurround): the strongest dimming wins, smoothly while regions fade.
 */
import { normalizeRegion, regionUniformShader, regionWeight, writeRegionUniform, type RegionShape } from './region-shape';
import { applyAreaShader, areaApply, combineAreaShader, combineAreaWeights, mirrorZ, type AreaApply, type EditorAreaBounds } from './area-shape';
import { outlinePoints, outlineSlabShader, outlineSlabWeight, resolveOutlineSlab, type OutlineSlab } from './outline-shape';
import { normalizeSplatObjectIds, splatObjectLaneCall } from './splat-objects';
import type { RegionColorEffect } from './splat-region-effects';
import {
    DEFAULT_COLOR_STRENGTH,
    DEFAULT_MENU_ALL_LABEL,
    DEFAULT_REGION_DIM,
    MAX_REGION_AREAS,
    MAX_REGION_OBJECT_AREAS,
    MAX_REGION_OUTLINE_POINTS,
    MAX_REGION_VOLUMES,
    MAX_REGIONS,
    REGION_PALETTE,
    type RegionDim,
    type RegionsConfig,
    type RegionsMenuConfig,
} from './regions';
import type { SceneTrigger } from '../types';
import type { SurfacePoint } from '../utils/surfacePolygon';

export const REGIONS_EFFECT_KEY = 'regions';
/** Regions colour and dim the splats before the other colour effects (haze), as focus did. */
export const REGIONS_EFFECT_ORDER = -1;
/** Rec. 709 luma, the weights focus regions always desaturated with. */
const LUMA = '0.2126, 0.7152, 0.0722';

export interface ResolvedRegion {
    id: string;
    name: string;
    /** #rrggbb */
    color: string;
    colorStrength: number;
    /** Dims the rest of the scene while it is on (null: leaves the rest alone). */
    dim: RegionDim | null;
    startsOn: boolean;
    inLayersMenu: boolean;
    section?: string;
    description?: string;
    showArea: boolean;
    triggers?: SceneTrigger[];
    /** Sum of its outline areas, scene units squared (the Layers menu shows it). */
    area: number;
    /** Drawable areas. */
    areaCount: number;
}

interface ResolvedRegionAreaBase {
    id: string;
    regionId: string;
    /** Index into ResolvedRegions.regions. */
    regionIndex: number;
    apply: AreaApply;
}
export interface ResolvedRegionOutline extends ResolvedRegionAreaBase {
    kind: 'outline';
    slab: OutlineSlab;
    /** The outline as the slab uses it (cleaned, capped): what the editor draws. */
    points: SurfacePoint[];
}
export interface ResolvedRegionVolume extends ResolvedRegionAreaBase {
    kind: RegionShape['shape'];
    region: RegionShape;
    /** Uniform slot (uRegionArea<slot>). */
    slot: number;
}
export interface ResolvedRegionObjects extends ResolvedRegionAreaBase {
    kind: 'splats';
    ids: string[];
    /** Flag lane. */
    slot: number;
}
export type ResolvedRegionArea = ResolvedRegionOutline | ResolvedRegionVolume | ResolvedRegionObjects;

export interface ResolvedRegionsMenu {
    show: boolean;
    title: string;
    selectionMode: 'multiple' | 'single';
    showAllOption: boolean;
    allLabel: string;
    startOpen: boolean;
}

export interface ResolvedRegions {
    regions: ResolvedRegion[];
    areas: ResolvedRegionArea[];
    menu: ResolvedRegionsMenu;
    showBounds: boolean;
}

const HEX = /^#[0-9a-f]{6}$/i;
const unit = (value: unknown, fallback: number): number => (typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback);
const text = (value: unknown, max = 120): string | undefined => (typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined);

function validTriggers(triggers: unknown): SceneTrigger[] | undefined {
    if (!Array.isArray(triggers)) return undefined;
    const valid = triggers.filter((t) => !!t && typeof t === 'object' && typeof (t as { type?: unknown }).type === 'string') as SceneTrigger[];
    return valid.length ? valid : undefined;
}

export function resolveRegionsMenu(menu: RegionsMenuConfig | undefined | null): ResolvedRegionsMenu {
    return {
        show: menu?.show !== false,
        title: text(menu?.title, 60) ?? 'Layers',
        selectionMode: menu?.selectionMode === 'single' ? 'single' : 'multiple',
        showAllOption: menu?.showAllOption !== false,
        allLabel: text(menu?.allLabel, 60) ?? DEFAULT_MENU_ALL_LABEL,
        startOpen: menu?.startOpen === true,
    };
}

/**
 * Validated, capped copy of the scene's regions: switched-off regions and areas, malformed
 * colours, outlines that can't be fitted, areas past the shared budgets (MAX_REGION_AREAS,
 * MAX_REGION_VOLUMES, MAX_REGION_OBJECT_AREAS, MAX_REGION_OUTLINE_POINTS) and regions left without
 * an area drop out. Undefined when nothing would be drawn.
 */
export function resolveRegions(config: RegionsConfig | undefined | null): ResolvedRegions | undefined {
    if (!config || typeof config !== 'object' || !Array.isArray(config.regions)) return undefined;
    const regions: ResolvedRegion[] = [];
    const areas: ResolvedRegionArea[] = [];
    const seenRegions = new Set<string>();
    const seenAreas = new Set<string>();
    let totalPoints = 0;
    let volumes = 0;
    let objects = 0;
    let index = 0;
    for (const r of config.regions) {
        if (regions.length >= MAX_REGIONS) break;
        if (!r || typeof r !== 'object' || typeof r.id !== 'string' || !r.id || seenRegions.has(r.id)) continue;
        seenRegions.add(r.id);
        const paletteIndex = index++;
        if (r.enabled === false) continue;
        const region: ResolvedRegion = {
            id: r.id,
            name: text(r.name) ?? 'Region',
            color: typeof r.color === 'string' && HEX.test(r.color) ? r.color.toLowerCase() : REGION_PALETTE[paletteIndex % REGION_PALETTE.length],
            colorStrength: unit(r.colorStrength, DEFAULT_COLOR_STRENGTH),
            dim: r.dimOutside && typeof r.dimOutside === 'object'
                ? { brightness: unit(r.dimOutside.brightness, DEFAULT_REGION_DIM.brightness), saturation: unit(r.dimOutside.saturation, DEFAULT_REGION_DIM.saturation) }
                : null,
            startsOn: r.startsOn !== false,
            inLayersMenu: r.inLayersMenu === true,
            ...(text(r.section, 60) ? { section: text(r.section, 60) } : {}),
            ...(text(r.description, 200) ? { description: text(r.description, 200) } : {}),
            showArea: r.showArea === true,
            ...(validTriggers(r.triggers) ? { triggers: validTriggers(r.triggers) } : {}),
            area: 0,
            areaCount: 0,
        };
        const regionIndex = regions.length;
        const own: ResolvedRegionArea[] = [];
        for (const a of Array.isArray(r.areas) ? r.areas : []) {
            if (areas.length + own.length >= MAX_REGION_AREAS) break;
            if (!a || typeof a !== 'object' || typeof a.id !== 'string' || seenAreas.has(a.id) || a.enabled === false) continue;
            const base = { id: a.id, regionId: r.id, regionIndex, apply: areaApply(a.apply) };
            if (a.shape === 'splats') {
                // No objects picked yet, or past the flag lanes (the editor stops adding them before this).
                const ids = normalizeSplatObjectIds(a.splatIds);
                if (!ids.length || objects >= MAX_REGION_OBJECT_AREAS) continue;
                seenAreas.add(a.id);
                own.push({ ...base, kind: 'splats', ids, slot: objects++ });
                continue;
            }
            if (a.shape === 'outline') {
                const slab = resolveOutlineSlab(a.points, a.depth, a.feather);
                // Not drawable yet (fewer than three points, on a line), or past the point budget.
                if (!slab || totalPoints + slab.fit.outline.length > MAX_REGION_OUTLINE_POINTS) continue;
                totalPoints += slab.fit.outline.length;
                seenAreas.add(a.id);
                own.push({ ...base, kind: 'outline', slab, points: outlinePoints(a.points) });
                region.area += slab.fit.area;
                continue;
            }
            // Sphere / box (unknown kinds read as a sphere, as area-shape.ts says), past the slots left out.
            if (volumes >= MAX_REGION_VOLUMES) continue;
            const shape = normalizeRegion({ ...a, shape: a.shape === 'box' ? 'box' : 'sphere' } as Partial<RegionShape>);
            seenAreas.add(a.id);
            own.push({ ...base, kind: shape.shape, region: shape, slot: volumes++ });
        }
        // A region without a drawable area draws nothing: it stays out of the menu, the API and
        // the selection logic (a one-at-a-time menu would otherwise open on an empty entry).
        if (!own.length) continue;
        region.areaCount = own.length;
        regions.push(region);
        areas.push(...own);
    }
    if (!regions.length) return undefined;
    return { regions, areas, menu: resolveRegionsMenu(config.menu), showBounds: config.showBounds !== false };
}

// ── Uniforms ────────────────────────────────────────────────────────────────────────────────

/** Region i's colour (rgb) and its current colour amount (colourStrength × fade) in a. */
export const regionColorUniform = (index: number): string => `uRegionColor${index}`;
/** Dim activations of regions 4k..4k+3 (x, y, z, w). */
export const regionDimUniform = (vector: number): string => `uRegionDim${vector}`;
/** x = brightness of the rest, y = saturation of the rest, z > 0 while any region dims. */
export const REGION_SURROUND_UNIFORM = 'uRegionSurround';
/** Sphere / box area slot: inverse transform (mat4) and `<name>P`.x = feather. */
export const regionAreaUniform = (slot: number): string => `uRegionArea${slot}`;
const DIM_LANES = ['x', 'y', 'z', 'w'] as const;
export const regionDimComponent = (index: number): string => `${regionDimUniform(index >> 2)}.${DIM_LANES[index & 3]}`;

/** rgb 0..1 from #rrggbb. */
export function regionRgb(hex: string): [number, number, number] {
    const valid = HEX.test(hex) ? hex : REGION_PALETTE[0];
    return [1, 3, 5].map((i) => parseInt(valid.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** One dimming region as the surround sees it this frame. */
export interface RegionDimFrame {
    /** The region's dim look, null when it doesn't dim. */
    dim: RegionDim | null;
    /** Its visibility fade, 0..1. */
    fade: number;
}

export interface RegionSurround {
    /** Brightness of everything outside the dimming regions (1 = unchanged). */
    brightness: number;
    /** Saturation of everything outside the dimming regions (1 = unchanged). */
    saturation: number;
    /** The strongest dimming fade (0 = nothing dims). */
    on: number;
    /** Per region: how much its inside is kept out of the dimming (0..1). */
    activations: number[];
}

/**
 * The surround look this frame. **Strongest dimming wins**: B = min over dimming regions of
 * mix(1, brightness_i, fade_i), S likewise with saturation, so the look moves smoothly while
 * regions fade. A region keeps its inside clear in proportion to its fade relative to the
 * strongest fading one (fade_i / max fade): a lone region fading in never dims its own inside,
 * and a region fading out while another stays on hands its inside over to that dimming smoothly.
 */
export function regionSurround(frames: ReadonlyArray<RegionDimFrame>): RegionSurround {
    let brightness = 1;
    let saturation = 1;
    let on = 0;
    for (const f of frames) {
        if (!f.dim) continue;
        const fade = unit(f.fade, 0);
        brightness = Math.min(brightness, 1 + (unit(f.dim.brightness, 1) - 1) * fade);
        saturation = Math.min(saturation, 1 + (unit(f.dim.saturation, 1) - 1) * fade);
        on = Math.max(on, fade);
    }
    const activations = frames.map((f) => (f.dim && on > 0 ? unit(f.fade, 0) / on : 0));
    return { brightness, saturation, on, activations };
}

/** One region's uniform values this frame. */
export interface RegionFrameLook {
    rgb: [number, number, number];
    /** colourStrength × fade. */
    amount: number;
}

/** Fill the effect's uniform arrays for one frame (colours, packed activations, surround). */
export function writeRegionsFrame(uniforms: Record<string, Float32Array>, looks: ReadonlyArray<RegionFrameLook>, surround: RegionSurround): void {
    looks.forEach((look, i) => {
        const target = uniforms[regionColorUniform(i)];
        if (!target) return;
        target[0] = look.rgb[0];
        target[1] = look.rgb[1];
        target[2] = look.rgb[2];
        target[3] = unit(look.amount, 0);
    });
    for (let k = 0; k * 4 < looks.length; k++) {
        const target = uniforms[regionDimUniform(k)];
        if (!target) continue;
        for (let c = 0; c < 4; c++) target[c] = surround.activations[k * 4 + c] ?? 0;
    }
    const s = uniforms[REGION_SURROUND_UNIFORM];
    if (s) {
        s[0] = surround.brightness;
        s[1] = surround.saturation;
        s[2] = surround.on;
        s[3] = 0;
    }
}

// ── CPU twins (tests, "which region is under this point") ──────────────────────────────────

/** One area's weight after `apply`; the point is in runtime scene coordinates. */
export function regionAreaWeight(area: ResolvedRegionArea, point: SurfacePoint, objectId?: string | null): number {
    const w =
        area.kind === 'splats'
            ? objectId && area.ids.includes(objectId) ? 1 : 0
            : area.kind === 'outline'
              ? outlineSlabWeight(area.slab, point)
              : regionWeight(area.region, mirrorZ(point));
    return area.apply === 'outside' ? 1 - w : w;
}

/** One region's weight: its areas combined (combineAreaWeights), as the shader does. */
export function regionWeightAt(resolved: Pick<ResolvedRegions, 'areas'>, regionIndex: number, point: SurfacePoint, objectId?: string | null): number {
    const own = resolved.areas.filter((a) => a.regionIndex === regionIndex);
    return own.length ? combineAreaWeights(own.map((a) => regionAreaWeight(a, point, objectId)), own.map((a) => a.apply)) : 0;
}

const mix = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * CPU twin of the per-splat shader body: `color` (rgb 0..1) with every region's weight at the
 * splat, its frame look and the surround applied.
 */
export function shadeRegions(color: readonly [number, number, number], weights: ReadonlyArray<number>, looks: ReadonlyArray<RegionFrameLook>, surround: RegionSurround): [number, number, number] {
    let c: [number, number, number] = [color[0], color[1], color[2]];
    let inside = 0;
    looks.forEach((look, i) => {
        const activation = surround.activations[i] ?? 0;
        if (!(look.amount > 0) && !(activation > 0)) return;
        const w = weights[i] ?? 0;
        c = [mix(c[0], look.rgb[0], w * look.amount), mix(c[1], look.rgb[1], w * look.amount), mix(c[2], look.rgb[2], w * look.amount)];
        inside = Math.max(inside, w * activation);
    });
    if (surround.on > 0) {
        const luma = c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
        const muted = c.map((v) => mix(luma, v, surround.saturation) * surround.brightness);
        c = [mix(muted[0], c[0], inside), mix(muted[1], c[1], inside), mix(muted[2], c[2], inside)];
    }
    return c;
}

// ── Shader ──────────────────────────────────────────────────────────────────────────────────

/** One area's weight function `name(c)` before `apply`. */
export function regionAreaShader(area: ResolvedRegionArea, name: string, lang: 'glsl' | 'wgsl'): string {
    if (area.kind === 'outline') return outlineSlabShader(area.slab, name, lang);
    if (area.kind === 'splats') {
        const lane = splatObjectLaneCall(REGIONS_EFFECT_KEY, area.slot);
        return lang === 'glsl' ? `float ${name}(vec3 c) { return ${lane}; }` : `fn ${name}(c: vec3f) -> f32 { return ${lane}; }`;
    }
    return regionUniformShader(area.kind, name, regionAreaUniform(area.slot), lang).trimEnd();
}

/** The full `region_regions` hook for `resolved` (uniforms named by region index and area slot). */
export function regionsShader(resolved: Pick<ResolvedRegions, 'regions' | 'areas'>, lang: 'glsl' | 'wgsl'): string {
    const glsl = lang === 'glsl';
    const u = (name: string) => (glsl ? name : `uniform.${name}`);
    const areaFns = resolved.areas.map((a, i) => regionAreaShader(a, `ssRegionArea${i}`, lang));
    const decls: string[] = [];
    const blocks: string[] = [];
    resolved.regions.forEach((_, ri) => {
        const own = resolved.areas.map((a, i) => (a.regionIndex === ri ? { term: applyAreaShader(`ssRegionArea${i}(center)`, a.apply), apply: a.apply } : null)).filter((x): x is { term: string; apply: AreaApply } => !!x);
        const colour = regionColorUniform(ri);
        decls.push(glsl ? `uniform vec4 ${colour};` : `uniform ${colour}: vec4f;`);
        if (!own.length) return;
        const weight = combineAreaShader(own.map((m) => m.term), own.map((m) => m.apply));
        const act = u(regionDimComponent(ri));
        const c = u(colour);
        blocks.push(
            glsl
                ? `if (${c}.a > 0.0 || ${act} > 0.0) { float w = ${weight}; color.rgb = mix(color.rgb, ${c}.rgb, w * ${c}.a); inside = max(inside, w * ${act}); }`
                : `if (${c}.a > 0.0 || ${act} > 0.0) { let w = ${weight}; *color = vec4f(mix((*color).rgb, ${c}.rgb, w * ${c}.a), (*color).a); inside = max(inside, w * ${act}); }`,
        );
    });
    for (let k = 0; k * 4 < resolved.regions.length; k++) decls.push(glsl ? `uniform vec4 ${regionDimUniform(k)};` : `uniform ${regionDimUniform(k)}: vec4f;`);
    decls.push(glsl ? `uniform vec4 ${REGION_SURROUND_UNIFORM};` : `uniform ${REGION_SURROUND_UNIFORM}: vec4f;`);
    const s = u(REGION_SURROUND_UNIFORM);
    const dim = glsl
        ? `if (${s}.z > 0.0) { vec3 muted = mix(vec3(dot(color.rgb, vec3(${LUMA}))), color.rgb, ${s}.y) * ${s}.x; color.rgb = mix(muted, color.rgb, inside); }`
        : `if (${s}.z > 0.0) { let rgb = (*color).rgb; let muted = mix(vec3f(dot(rgb, vec3f(${LUMA}))), rgb, ${s}.y) * ${s}.x; *color = vec4f(mix(muted, rgb, inside), (*color).a); }`;
    const body = [glsl ? 'float inside = 0.0;' : 'var inside: f32 = 0.0;', ...blocks, dim].join('\n    ');
    const main = glsl
        ? `void region_${REGIONS_EFFECT_KEY}(vec3 center, inout vec4 color) {\n    ${body}\n}`
        : `fn region_${REGIONS_EFFECT_KEY}(center: vec3f, color: ptr<function, vec4f>) {\n    ${body}\n}`;
    return [decls.join('\n'), ...areaFns, main].join('\n');
}

/** Write a sphere / box area's transform (animated or authored) into its uniform slot. */
export function writeRegionAreaUniform(uniforms: Record<string, Float32Array>, slot: number, shape: RegionShape): void {
    const name = regionAreaUniform(slot);
    if (!uniforms[name]) uniforms[name] = new Float32Array(16);
    if (!uniforms[`${name}P`]) uniforms[`${name}P`] = new Float32Array(4);
    writeRegionUniform({ matrix: uniforms[name], params: uniforms[`${name}P`] }, shape);
}

/**
 * The effect to install, or undefined when nothing would be drawn. `uniforms` holds the arrays the
 * caller mutates to switch / fade / recolour / move regions (created here when missing, reused
 * otherwise, so a rebuild with the same shader keeps driving the installed effect). `bounds` is the
 * caller's array of editor bounds, mutated in place as regions show and hide.
 */
export function buildRegionsEffect(resolved: ResolvedRegions | undefined, uniforms: Record<string, Float32Array>, bounds: EditorAreaBounds[] = []): RegionColorEffect | undefined {
    if (!resolved || !resolved.areas.length) return undefined;
    const used: Record<string, Float32Array> = {};
    const take = (name: string, size: number) => {
        if (!uniforms[name] || uniforms[name].length !== size) uniforms[name] = new Float32Array(size);
        used[name] = uniforms[name];
    };
    resolved.regions.forEach((_, i) => take(regionColorUniform(i), 4));
    for (let k = 0; k * 4 < resolved.regions.length; k++) take(regionDimUniform(k), 4);
    take(REGION_SURROUND_UNIFORM, 4);
    const objectSets: string[][] = [];
    for (const a of resolved.areas) {
        if (a.kind === 'splats') objectSets[a.slot] = a.ids;
        if (a.kind !== 'sphere' && a.kind !== 'box') continue;
        writeRegionAreaUniform(uniforms, a.slot, a.region);
        used[regionAreaUniform(a.slot)] = uniforms[regionAreaUniform(a.slot)];
        used[`${regionAreaUniform(a.slot)}P`] = uniforms[`${regionAreaUniform(a.slot)}P`];
    }
    return {
        key: REGIONS_EFFECT_KEY,
        order: REGIONS_EFFECT_ORDER,
        glsl: regionsShader(resolved, 'glsl'),
        wgsl: regionsShader(resolved, 'wgsl'),
        bounds,
        uniforms: used,
        ...(objectSets.length ? { objectSets } : {}),
    };
}
